import { STColorSchemeIndex, STFontCollectionIndex, STSchemeColorVal } from '@tencent/slide-sdk-pb-model';
import { BaseContext } from './base-context';
import type {
    ICTBackgroundProperties,
    ICTBaseStyles,
    ICTColorMapping,
    ICTColorScheme,
    ICTEffectList,
    ICTFillProperties,
    ICTFontCollection,
    ICTLineProperties,
    ICTTextCharacterProperties,
    IEGColorChoice,
} from '@tencent/slide-sdk-pb-model';
import type { DeepReadonly } from 'ts-essentials';
import type { IThemeDataModel } from '../model/theme-data-model';

const DEFAULT_FONT = 'Arial';

const SchemeFontMap = {
    ['+mn-lt']: ['minorFont', 'latin'],
    ['+mn-ea']: ['minorFont', 'ea'],
    ['+mj-lt']: ['majorFont', 'latin'],
    ['+mj-ea']: ['majorFont', 'ea'],
} as const;

const FontCollectionKeys = {
    [STFontCollectionIndex.PBDEFAULT]: undefined,
    [STFontCollectionIndex.major]: 'majorFont',
    [STFontCollectionIndex.minor]: 'minorFont',
    [STFontCollectionIndex.none]: undefined,
} as const;

const SchemeColorValList = Object.values(STSchemeColorVal).filter(
    (val): val is STSchemeColorVal => typeof val !== 'string',
);

export interface IThemeContext {
    resolveFontTypeface(typeface: string): string | undefined;
    getSchemeColorMap(): Map<STSchemeColorVal, DeepReadonly<IEGColorChoice>>;
    getFillStyleByIndex(idx?: number): DeepReadonly<ICTFillProperties> | undefined;
    getLineStyleByIndex(idx?: number): DeepReadonly<ICTLineProperties> | undefined;
    getBackgroundStyleByIndex(idx?: number): DeepReadonly<ICTBackgroundProperties> | undefined;
    getEffectListStyleByIndex(idx?: number): DeepReadonly<ICTEffectList> | undefined;
    getFontCollectionStyleByIndex(idx?: STFontCollectionIndex): DeepReadonly<ICTFontCollection> | undefined;
}

export class ThemeContext extends BaseContext implements IThemeContext {
    public static shouldUseSchemeFont(typeface: string): boolean {
        return /\+m[nj]-../.test(typeface);
    }

    public static getFontStyleByFontCollection(
        fontCollection?: DeepReadonly<ICTFontCollection>,
    ): DeepReadonly<Pick<ICTTextCharacterProperties, 'latin' | 'ea'>> {
        return {
            latin: {
                typeface: ThemeContext.getSchemeFontInCollection('latin', fontCollection),
            },
            ea: {
                typeface: ThemeContext.getSchemeFontInCollection('ea', fontCollection),
            },
        };
    }

    public static getSchemeFontInCollection(
        region: 'latin' | 'ea',
        fontCollection?: DeepReadonly<ICTFontCollection>,
    ): string | undefined {
        // 暂不支持多语言字体，ea(East Asia) 如果有多语言，只使用汉语(Hans)
        if (region === 'ea') {
            return (
                fontCollection?.ea?.typeface || // 优先使用 ea 自身设置的字体
                fontCollection?.font?.find((i) => i.script === 'Hans')?.typeface || // ea 自身未设置字体，如果设置了 Hans 字体，使用 Hans
                fontCollection?.latin?.typeface || // 未设置东亚字体，降级使用 latin
                DEFAULT_FONT // latin 也未设置字体，使用默认字体
            );
        }
        return fontCollection?.[region]?.typeface || DEFAULT_FONT;
    }

    private cacheThemeElements: DeepReadonly<ICTBaseStyles> | undefined;
    private schemeColorMap: Map<STSchemeColorVal, DeepReadonly<IEGColorChoice>>;
    private theme?: IThemeDataModel;

    private aliasColorMap?: DeepReadonly<ICTColorMapping>;

    private get themeElements(): DeepReadonly<ICTBaseStyles> | undefined {
        return this.theme?.props.themeElements;
    }

    public constructor({
        theme,
        aliasColorMap,
    }: {
        theme?: IThemeDataModel;
        aliasColorMap?: DeepReadonly<ICTColorMapping>;
    }) {
        super();
        this.theme = theme;
        this.aliasColorMap = aliasColorMap;
    }

    public resolveFontTypeface(typeface: string): string | undefined {
        // 不支持的主题字体格式，暂不进行处理直接返回
        if (!Object.keys(SchemeFontMap).includes(typeface)) {
            return typeface;
        }

        // 使用主题字体
        const [importance, region] = SchemeFontMap[typeface as keyof typeof SchemeFontMap];
        return ThemeContext.getSchemeFontInCollection(region, this.themeElements?.fontScheme?.[importance]);
    }

    public getFontCollectionStyleByIndex(
        index: STFontCollectionIndex = STFontCollectionIndex.PBDEFAULT,
    ): DeepReadonly<ICTFontCollection> | undefined {
        const importanceKey = FontCollectionKeys[index];
        if (!importanceKey) {
            return;
        }
        return this.themeElements?.fontScheme?.[importanceKey];
    }

    public getSchemeColorMap(): Map<STSchemeColorVal, DeepReadonly<IEGColorChoice>> {
        // 如果 themeElements 发生变化，更新 schemeColorMap 缓存
        if (this.cacheThemeElements !== this.themeElements) {
            this.cacheThemeElements = this.themeElements;
            this.schemeColorMap = this.createSchemeColorMap();
        }
        return this.schemeColorMap;
    }

    public getFillStyleByIndex(idx?: number) {
        if (!idx) return;

        return this.themeElements?.fmtScheme?.fillStyleLst?.egFillProperties?.[idx - 1];
    }

    public getLineStyleByIndex(idx?: number) {
        if (!idx) return;

        return this.themeElements?.fmtScheme?.lnStyleLst?.ln?.[idx - 1];
    }

    public getBackgroundStyleByIndex(idx?: number) {
        if (!idx) return;

        return this.themeElements?.fmtScheme?.bgFillStyleLst?.egFillProperties?.[idx - 1001];
    }

    public getEffectListStyleByIndex(idx?: number) {
        if (!idx) return;

        return this.themeElements?.fmtScheme?.effectStyleLst?.effectStyle?.[idx - 1]?.effectLst;
    }

    private createSchemeColorMap(): Map<STSchemeColorVal, DeepReadonly<IEGColorChoice>> {
        const schemeColorMap = new Map<STSchemeColorVal, DeepReadonly<IEGColorChoice>>();
        const defaultColor = { srgbClr: { val: '000000' } };

        const { themeElements, aliasColorMap } = this;

        for (const schemeColorVal of SchemeColorValList) {
            schemeColorMap.set(schemeColorVal, defaultColor);

            if (schemeColorVal === STSchemeColorVal.PBDEFAULT) {
                continue;
            }

            if (schemeColorVal === STSchemeColorVal.phClr) {
                schemeColorMap.set(schemeColorVal, { schemeClr: { val: STSchemeColorVal.phClr } });
                continue;
            }

            if (!themeElements?.clrScheme) {
                continue;
            }

            const colorValKey = STSchemeColorVal[schemeColorVal];

            const alias = aliasColorMap?.[colorValKey as keyof Omit<ICTColorMapping, 'extLst'>];
            if (alias) {
                const aliasColor =
                    themeElements?.clrScheme[
                        STColorSchemeIndex[alias] as keyof Omit<ICTColorScheme, 'name' | 'extLst'>
                    ];
                if (aliasColor) {
                    schemeColorMap.set(schemeColorVal, aliasColor);
                }
                continue;
            }

            const themeColor = themeElements?.clrScheme[colorValKey as keyof Omit<ICTColorScheme, 'name' | 'extLst'>];
            if (themeColor) {
                schemeColorMap.set(schemeColorVal, themeColor);
                continue;
            }
        }

        return schemeColorMap;
    }
}
